import { FormEvent, useEffect, useState } from "react"
import { authenticateGuestByBooking } from "./api"

interface Props {
  open: boolean
  slug: string
  authMode: "pms" | "ical"
  requiresPinForSensitive?: boolean
  onClose: () => void
  onSuccess: (guestToken: string) => void | Promise<void>
}

export function UnlockStayModal({ open, slug, authMode, requiresPinForSensitive, onClose, onSuccess }: Props) {
  const [lastName, setLastName] = useState("")
  const [checkIn, setCheckIn] = useState("")
  const [checkOut, setCheckOut] = useState("")
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setBusy(false)
    setError(null)
    setLastName("")
    setCheckIn("")
    setCheckOut("")
  }, [open])

  if (!open) return null

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (busy) return

    if (authMode === "pms") {
      if (!lastName.trim() || !checkIn.trim()) {
        setError("Use the last name in your booking and your check-in date.")
        return
      }
    } else if (!checkIn.trim() || !checkOut.trim()) {
      setError("Use your check-in and check-out dates to continue.")
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await authenticateGuestByBooking(
        slug,
        lastName.trim(),
        checkIn.trim(),
        authMode === "ical" ? checkOut.trim() : undefined,
      )
      try {
        await onSuccess(result.guest_token)
        onClose()
      } catch {
        setError("Verified, but couldn't load your guidebook. Please try again.")
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not verify those details.")
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="gb-unlock-modal" role="presentation" onClick={onClose}>
      <div
        className="gb-unlock-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="gb-unlock-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="gb-unlock-modal__title" id="gb-unlock-modal-title">Unlock stay details</h2>
        <p className="gb-unlock-modal__subtitle">
          Confirm your booking to view private access details.
        </p>

        <form onSubmit={onSubmit} className="gb-unlock-modal__form">
          {authMode === "pms" && (
            <label className="gb-unlock-modal__field">
              Last name
              <input
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(event) => setLastName(event.target.value)}
                placeholder="Andersson"
                required
              />
            </label>
          )}

          <label className="gb-unlock-modal__field">
            Check-in date
            <input
              type="date"
              value={checkIn}
              onChange={(event) => setCheckIn(event.target.value)}
              required
            />
          </label>

          {authMode === "ical" && (
            <label className="gb-unlock-modal__field">
              Check-out date
              <input
                type="date"
                value={checkOut}
                onChange={(event) => setCheckOut(event.target.value)}
                required
              />
            </label>
          )}

          {error && (
            <div className="gb-unlock-modal__error">
              {error}
            </div>
          )}

          {requiresPinForSensitive && authMode === "ical" && (
            <div className="gb-unlock-modal__note">
              For security, this property protects entry details with a short access PIN.
            </div>
          )}

          <div className="gb-unlock-modal__actions">
            <button type="button" className="gb-unlock-modal__ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="guidebook__error-retry" disabled={busy}>
              {busy ? "Confirming..." : "Unlock stay details"}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
